import { debateTypes } from './../../components/DebateArena/debateFormatsDataV3';
import { v4 as uuidv4 } from 'uuid';
import { EventEmitter } from 'events';

export class DebateEngine {
	constructor({ debateId, debateTypeId, state }) {
		this.debateId = debateId;
		this.debateType = debateTypes.find(d => d.id === debateTypeId);
		if (!this.debateType) throw new Error(`Unknown debate type: ${debateTypeId}`);
		this.state = state || this._initialState();
		this.eventsEmitter = new EventEmitter();
		this._listeners = new Set();
	}

	/* ----------------------------------
	* Initialization
	* ---------------------------------- */
	_initialState() {
		return {
		  status: 'idle', // idle | active | paused | ended
		  turnIndex: 0,
		  currentRound: 0,
		  totalRounds: 0,
		  roundActive: false,
		  turnStartedAt: null,
		  remainingTimeForTurn: null,
		  teamTimeRemaining: {},
		  teams: [],
		  turns: [],
		  messages: [],
		  version: 1,
		  scores: {},
		  speakers: {}, // { uid: { teamId, username } }
		  events: [],
		  currentSpeaker: null,
		  spectators: {
			questions: [],
			comments: [],
			votes: { pre: {}, post: {} },
			speakerQueue: [],
			activeGuestSpeakers: {}
		  },
		  lastTickAt: null
		};
	}

	_bumpVersion(reason) {
		this.state.version += 1;
		this.state.events.push({
		  type: 'VERSION_BUMP',
		  reason,
		  at: Date.now(),
		  version: this.state.version
		});
	}

/** ----------------------------
   * Event Emitter
   * ---------------------------- */
	onEvent(listener) {
		this._listeners.add(listener);
	}

	offEvent(listener) {
		this._listeners.delete(listener);
	}

	_emitListenerEvent(event) {
		// Event structure: { type, payload, at: timestamp }
		//const e = { ...event, at: Date.now() };
		this._listeners.forEach(fn => fn(event));
		// Also push to internal events array for history
		//this.state.events.push(e);
	}

	/*_emit(event) {
		this.eventsEmitter.emit('event', event);
	}

	on(eventType, callback) {
		this.eventsEmitter.on(eventType, callback);
	}

	off(eventType, callback) {
		this.eventsEmitter.off(eventType, callback);
	}*/

	_emitEvent(type, payload = {}) {
	  this.state.events.push({
		type,
		...payload,
		at: Date.now(),
		version: this.state.version
	  });
	  
	  this._emitListenerEvent({
		type,
		...payload,
		at: Date.now(),
		version: this.state.version
	  });
	  
	  /*this._emit({
		type,
		...payload,
		at: Date.now(),
		version: this.state.version
	  });*/
	  
	}

	deriveAllowsPOI(turn, debateType) {
		// Explicitly NO POI in these turn types
		if (['reply', 'summary', 'closing', 'cx'].includes(turn.type)) {
			return false;
		}

		// Fishbowl / Town Hall: POI always allowed during active discussion
		if (['fishbowl', 'townhall'].includes(debateType.id)) {
			return true;
		}

		// Parliamentary-style speeches allow POI
		if (turn.type === 'speech') {
			return true;
		}

		return false;
	}

	startDebate() {
		if (this.state.status !== 'idle') return;

		const turns = this.debateType.turns.map((turn, index) => ({
			index,
			...turn,
			messages: [],
			// Track remaining time per speaker
			speakerTimers: {}, 
			allowsPOI: this.deriveAllowsPOI(turn, this.debateType)
		}));

		const teams = [...new Set(turns.map(t => t.team))];
		const teamTimeRemaining = {};
		teams.forEach(team => {
		const total = turns.filter(t => t.team === team).reduce((acc, t) => acc + t.durationSec, 0);
		teamTimeRemaining[team] = total;
		});

		// Initialize speakerTimers
		turns.forEach(turn => {
		const speakers = this.getTurnSpeakers(turn);
		speakers.forEach(s => {
		  turn.speakerTimers[s.speakerId] = turn.durationSec; // full turn duration initially
		});
		});

		this.state = {
		  ...this.state,
		  status: 'active',
		  turns,
		  teams,
		  teamTimeRemaining,
		  turnIndex: 0,
		  currentRound: 1,
		  roundActive: true,
		  turnStartedAt: Date.now(),
		  remainingTimeForTurn: turns[0].durationSec,
		  lastTickAt: Date.now()
		};
		
		this._emitEvent('DEBATE_STARTED', {
			round: this.state.currentRound
		});

		this._emitEvent('ROUND_STARTED', {
			round: this.state.currentRound
		});

		this._emitEvent('TURN_STARTED', {
			round: this.state.currentRound,
			turnIndex: 0,
			turnType: turns[0].type,
			team: turns[0].team
		});
		
		if (turns[0].type === 'cross-examination') {
		  this._emitEvent('CX_WINDOW_OPENED', {
			round: this.state.currentRound,
			turnIndex: 0
		  });
		}

		if (turns[0]?.allowsPOI) {
		  this._emitEvent('POI_WINDOW_OPENED', {
			round: this.state.currentRound,
			turnIndex: 0
		  });
		}
  
		this._bumpVersion('startDebate');
	}

	setParticipants({ teams, speakers }) {
		this.state.teams = teams;
		this.state.speakers = speakers; // { uid: { username, teamId } }
	}

	setDebateType({ debateId, debateTypeId }) {
		this.debateId = debateId;
		this.debateType = debateTypes.find(d => d.id === debateTypeId);
		if (!this.debateType) throw new Error(`Unknown debate type: ${debateTypeId}`);
	}

	getFlowControllerRole() {
		if (this.debateType.roles.moderator?.exists) {
			return 'moderator';
		}

		if (this.debateType.roles.judge?.exists &&
			this.debateType.roles.judge?.fallbackFlowControl) {
			return 'judge';
		}

		return 'debater';
	}

	canUserControlFlow(userRole) {
  		const controller = this.getFlowControllerRole();
  		return userRole === controller;
	}

	/* ----------------------------------
	* Permissions
	* ---------------------------------- */
	can(role, action) {
		const roles = this.debateType.roles;
		if (!roles) return false;
		if (role === 'moderator') return roles.moderator?.exists && roles.moderator.can?.includes(action);

		if (role === 'judge') {
		  if (!roles.judge?.exists) return false;
		  if (roles.judge.fallbackFlowControl && roles.moderator.can?.includes(action)) return true;
		  return roles.judge.can?.includes(action);
		}

		if (role === 'debater') {
		  if (this.getFlowControllerRole() !== 'debater') return false;
		  if (roles.moderator.can?.includes(action)) return true;
		  return false;
		}
		return false;
	}

	_canSpectator(feature) {
		const spectators = this.debateType.spectators;
		return spectators?.canParticipate && spectators.features?.includes(feature);
	}

	/* ----------------------------------
	* Time Management
	* ---------------------------------- */
	tick(seconds = 1) {
		if (this.state.status !== 'active') return;
		const now = Date.now();
		const delta = seconds ?? Math.floor((now - this.state.lastTickAt) / 1000);

		const turn = this.getActiveTurn();
		if (!turn) return;

		this.state.remainingTimeForTurn -= delta;
		if (this.state.teamTimeRemaining?.[turn.team] != null) {
		  this.state.teamTimeRemaining[turn.team] = Math.max(this.state.teamTimeRemaining[turn.team] - delta, 0);
		}
		this.state.lastTickAt = now;

		if (this.state.remainingTimeForTurn <= 0 || this.state.teamTimeRemaining[turn.team] <= 0) {
		  if (this.state.remainingTimeForTurn <= 0)this.state.remainingTimeForTurn = 0;
		  if (this.state.teamTimeRemaining[turn.team] <= 0)this.state.teamTimeRemaining[turn.team] = 0;
		  this.endTurn();
		}

		this._pruneExpiredGuestSpeakers();
		this._bumpVersion('tick');
	}

	remainingTimeForTurn() {
		return Math.max(0, this.state.remainingTimeForTurn ?? 0);
	}

	remainingTimeForTeam(team) {
		return Math.max(0, this.state.teamTimeRemaining?.[team] ?? 0);
	}

	remainingTimeForSpeaker(speakerId) {
		const turn = this.getActiveTurn();
		if (!turn) return 0;

		// queued guest speakers
		const guest = this.state.spectators.activeGuestSpeakers[speakerId];
		if (guest) return Math.max(0, Math.floor((guest.expiresAt - Date.now()) / 1000));

		// team speaker
		return turn.speakerTimers?.[speakerId] ?? 0;
		
		//const activeSpeakers = this.getTurnSpeakers(turn);
		//if (activeSpeakers.some(s => s.uid === speakerId)) return this.remainingTimeForTurn();

		return 0;
	}

	/* ----------------------------------
	* Turn Flow
	* ---------------------------------- */
	getActiveTurn() {
		if (this.state.status === 'ended') return null;
		return this.state.turns?.[this.state.turnIndex] || null;
	}

	setCurrentSpeaker(speakerId) {
		this.state.currentSpeaker = speakerId;
	}

	getTurnSpeakers(turn) {
		if (!turn) return [];
		const teamMembers = Object.entries(this.state.speakers)
		  .filter(([_, s]) => s.teamId === turn.team)
		  .map(([uid, s]) => ({ uid, ...s, currentSpeaker: this.state.currentSpeaker === uid }));

		if (turn.type === 'cross-examination') return teamMembers.slice(0, 1);
		if (['speech', 'rebuttal', 'reply', 'opening', 'closing'].includes(turn.type)) return teamMembers;

		return [];
	}

	getActiveSpeakerQueue() {
		const now = Date.now();
		return (this.state.spectators.speakerQueue || []).filter(s => !s.expiresAt || s.expiresAt > now);
	}

	getActiveSpeakers() {
		const turn = this.getActiveTurn();
		if (!turn) return [];
		const teamId = turn.team;

		const teamSpeakers = Object.entries(this.state.speakers)
		  .filter(([_, s]) => s.teamId === teamId)
		  .map(([uid, s]) => ({ uid, ...s }));

		const guestSpeakers = Object.entries(this.state.spectators.activeGuestSpeakers)
		  .filter(([uid, meta]) => meta.teamId === teamId)
		  .map(([uid]) => ({ uid, ...this.state.speakers[uid], guest: true }));

		const queuedSpeakers = this.getActiveSpeakerQueue().filter(q => q.teamId === teamId);

		return [...teamSpeakers, ...guestSpeakers, ...queuedSpeakers].map(s => ({
		  uid: s.uid,
		  speakerId: s.speakerId,
		  username: s.username,
		  remainingTime: this.remainingTimeForSpeaker(s.uid),
		  turnDescription: turn.description
		}));
	}

	endTurn() {
		if (this.state.status !== 'active') return;

		const turn = this.getActiveTurn();
		if (!turn) return;
		
		this._emitEvent('TURN_ENDED', {
		  round: this.state.currentRound,
		  turnIndex: this.state.turnIndex,
		  team: turn.team
		});
		
		// Decrement team timer by elapsed time
		if (this.state.turnStartedAt && this.state.teamTimeRemaining[turn.team] != null) {
			const elapsedSec = (Date.now() - this.state.turnStartedAt) / 1000;
			this.state.teamTimeRemaining[turn.team] = Math.max(
			  this.state.teamTimeRemaining[turn.team] - elapsedSec,
			  0
			);
		}

		// Log turn end
		this.state.events.push({
			type: 'END_TURN',
			turnIndex: this.state.turnIndex,
			at: Date.now(),
		});
		
		if (turn.type === 'cross-examination') {
		  this._emitEvent('CX_WINDOW_CLOSED', {
			round: this.state.currentRound,
			turnIndex: this.state.turnIndex
		  });
		}

		if (turn?.allowsPOI) {
		  this._emitEvent('POI_WINDOW_CLOSED', {
			round: this.state.currentRound,
			turnIndex: this.state.turnIndex
		  });
		}
		
		const nextIndex = this.state.turnIndex + 1;

		// Check if we finished all turns for the current round
		const turnsPerRound = this.debateType.turns.length; // or calculate dynamically if repeats per round
		if (nextIndex >= turnsPerRound) {
			// Round finished
			this.state.roundActive = false;
			this._emitEvent('ROUND_ENDED', {
				round: this.state.currentRound
			});
			// Check if there are more rounds configured
			//console.log("rounds: ", this.state.currentRound, this.state.totalRounds, this.debateType.rounds);
			if (this.state.currentRound < (this.state.totalRounds || this.debateType.rounds || 1)) {
				// Start next round
				const teams = [...new Set(this.state.turns.map(t => t.team))];
				teams.forEach(team => {
				const total = this.state.turns.filter(t => t.team === team).reduce((acc, t) => acc + t.durationSec, 0);
				this.state.teamTimeRemaining[team] = total;
				});

				// Initialize speakerTimers
				this.state.turns.forEach(turn => {
					const speakers = this.getTurnSpeakers(turn);
					speakers.forEach(s => {
					turn.speakerTimers[s.speakerId] = turn.durationSec; // full turn duration initially
					});
				});
				this.state.currentRound += 1;
				this.state.roundActive = true;
				this.state.turnIndex = 0; // reset turn index for new round
			} else {
				// No more rounds � debate ends
				this.state.status = 'ended';
				this._emitEvent('DEBATE_ENDED', {});
				this._bumpVersion('endDebate');
				return;
			}
		} else {
			// Next turn within current round
			this.state.turnIndex = nextIndex;
		}

		const nextTurn = this.state.turns[this.state.turnIndex];
		this.state.turnStartedAt = Date.now();
		this.state.remainingTimeForTurn = nextTurn.durationSec;
		this._emitEvent('TURN_STARTED', {
		  round: this.state.currentRound,
		  turnIndex: this.state.turnIndex,
		  turnType: nextTurn.type,
		  team: nextTurn.team
		});
		if (nextTurn.type === 'cross-examination') {
		  this._emitEvent('CX_WINDOW_OPENED', {
			round: this.state.currentRound,
			turnIndex: nextIndex
		  });
		}
		this._pruneExpiredGuestSpeakers();
		this._bumpVersion('endTurn');
	}

	skipSpeaker(speakerId) {
		const turn = this.getActiveTurn();
		if (!turn) return;

		const now = Date.now();

		// 1?? Remove speaker from Fishbowl/Town Hall queue
		if (this.state.spectators?.speakerQueue) {
			this.state.spectators.speakerQueue = this.state.spectators.speakerQueue.filter(
			  s => s.speakerId !== speakerId && s.uid !== speakerId
			);
		}

		// 2?? Remove active guest speaker if exists
		if (this.state.spectators?.activeGuestSpeakers?.[speakerId]) {
			delete this.state.spectators.activeGuestSpeakers[speakerId];
		}

		// 3?? Adjust timers: For simplicity, assume speaker had remainingTimeForTurn
		// We'll decrement turn and team remaining times by an estimated "default" duration
		const remainingForSpeaker = this.remainingTimeForSpeaker(speakerId) ?? 0;

		if (remainingForSpeaker > 0) {
			this.state.remainingTimeForTurn = Math.max(this.state.remainingTimeForTurn - remainingForSpeaker, 0);
			if (turn.team && this.state.teamTimeRemaining?.[turn.team] != null) {
			  this.state.teamTimeRemaining[turn.team] = Math.max(
				this.state.teamTimeRemaining[turn.team] - remainingForSpeaker,
				0
			  );
			}
			if (turn.speakerTimers) {
			  turn.speakerTimers[speakerId] = 0;
			}
		}
		
		this._emitEvent('SPEAKER_SKIPPED', {
			speakerId,
			turnIndex: this.state.turnIndex,
			round: this.state.currentRound
		});

		this._bumpVersion('skipSpeaker');

		// 5?? End turn if no remaining speakers
		const activeSpeakers = this.getActiveSpeakers();
			if (!activeSpeakers.length || this.state.remainingTimeForTurn <= 0) {
			this.endTurn();
		}
	}

	/* ----------------------------------
	* Audio Messages
	* ---------------------------------- */
	async addMessage({ speakerId, team, durationSec, audioUrl }) {
		const turn = this.getActiveTurn();
		if (!turn || !this.canRecord(speakerId)) return false;

		if (durationSec > this.remainingTimeForTurn()) return false;

		turn.messages.push({ speakerId, team, durationSec, audioUrl, at: Date.now() });

		// Subtract only from this speaker
		turn.speakerTimers[speakerId] -= durationSec;

		// Update turn & team totals
		this.state.remainingTimeForTurn -= durationSec;
		this.state.teamTimeRemaining[team] -= durationSec;

		this._pruneExpiredGuestSpeakers();

		// Optional scoring
		if (!this.state.scores[team]) this.state.scores[team] = 0;
		this.state.scores[team] += Math.floor(durationSec / 10);

		this.state.currentSpeaker = null; // reset current speaker after message
		this._bumpVersion('addMessage');

		if (this.state.remainingTimeForTurn <= 0) await this.endTurn();
		return true;
	}

	canRecord(speakerId) {
		const turn = this.getActiveTurn();
		if (!turn) return false;
		const speaker = this.state.speakers[speakerId];
		const guest = this.state.spectators.activeGuestSpeakers[speakerId];
		if (!speaker && !guest) return false;
		if (speaker?.teamId !== turn.team && guest?.teamId !== turn.team) return false;
		return this.remainingTimeForSpeaker(speakerId) > 0;
	}
	/* ----------------------------------
	* Scoring (Optional Feature)
	* ---------------------------------- */

	configureRounds(rounds) {
	  if (!Number.isInteger(rounds) || rounds < 1) {
		throw new Error('Rounds must be an integer >= 1');
	  }
	  this.state.totalRounds = rounds;
	  this._bumpVersion('configureRounds');
	}

	updateScores(targetId, points) {
		this.state.scores[targetId] = (this.state.scores[targetId] || 0) + points;
		this._bumpVersion('updateScores');
		//console.log(`Scores updated: ${targetId} now has ${this.state.scores[targetId]} points`);
	}

	submitBallot(ballot) {
		this.state.ballot = ballot;
	}

	declareWinner(winner) {
		this.state.winner = winner;
		this.state.status = 'ended';
	}

	/* ----------------------------------
	* Pause / Resume
	* ---------------------------------- */

	pauseDebate() {
		if (this.state.status === 'active') {
		  this.state.status = 'paused';
		}
	}

	resumeDebate() {
		if (this.state.status === 'paused') {
		  this.state.status = 'active';
		  this.state.lastTickAt = Date.now();
		}
	}

	/* ----------------------------------
	* spectator
	* ---------------------------------- */

	submitQuestion({ spectatorId, text }) {
	  this.ensureInitialized();

	  if (!this._canSpectator('questions')) {
		throw new Error('Spectators cannot submit questions in this debate');
	  }

	  this.state.spectators.questions.push({
		id: uuidv4(),
		spectatorId,
		text,
		approved: false,
		at: Date.now()
	  });

	  this._bumpVersion('submitQuestion');
	}

	approveQuestion(questionId) {
	  const q = this.state.spectators.questions.find(q => q.id === questionId);
	  if (!q) throw new Error('Question not found');

	  q.approved = true;
	  this._bumpVersion('approveQuestion');
	}

	addComment({ userId, text }) {
	  this.ensureInitialized();

	  if (!this._canSpectator('comments')) {
		throw new Error('Comments not allowed');
	  }

	  this.state.spectators.comments.push({
		id: uuidv4(),
		userId,
		text,
		at: Date.now()
	  });

	  this._bumpVersion('addComment');
	}

	castVote({ spectatorId, team, phase }) {
	  this.ensureInitialized();

	  if (
		(phase === 'pre' && !this._canSpectator('preVote')) ||
		(phase === 'post' && !this._canSpectator('postVote'))
	  ) {
		throw new Error('Voting not allowed');
	  }
	  this.state.spectators.votes[phase][spectatorId] = team;
	  this._bumpVersion(`castVote:${phase}`);
	}

	getVoteTally(phase) {
	  const votes = this.state.spectators.votes[phase];
	  return Object.values(votes).reduce((acc, team) => {
		acc[team] = (acc[team] || 0) + 1;
		return acc;
	  }, {});
	}

	joinSpeakerQueue({ spectatorId }) {
	  var durationSec = 60;
	  this.ensureInitialized();

	  if (!this._canSpectator('joinSpeakerQueue')) {
		throw new Error('Speaker queue not enabled');
	  }

	  if (this.state.spectators.speakerQueue.includes(spectatorId)) return;

	  this.state.spectators.speakerQueue.push({
		  speakerId: spectatorId,
		  addedAt: Date.now(),
		  expiresAt: Date.now() + durationSec * 1000
		});
	  this._bumpVersion('joinSpeakerQueue');
	}

	skipSpectatorsSpeaker(speakerId) {
		if (!this.state.spectators?.speakerQueue) return;
		this.state.spectators.speakerQueue = this.state.spectators.speakerQueue.filter(
		  s => s.speakerId !== speakerId
		);
	}

	nextQueuedSpeaker() {
	  return this.state.spectators.speakerQueue.shift() || null;
	}

	submitPOI({ spectatorId, text }) {
		this.ensureInitialized();

		if (!this._canSpectator('pointsOfInformation')) {
			throw new Error('POIs not allowed');
		}

		const turn = this.getActiveTurn();
		if (!turn) return;

		this.state.spectators.questions.push({
			id: uuidv4(),
			spectatorId,
			turnId: turn.id,
			text,
			type: 'POI',
			approved: false,
			at: Date.now()
		});

		this._bumpVersion('submitPOI');
	}

	requestToSpeak(userId) {
		if (!this._canSpectator('joinSpeakerQueue')) {
			throw new Error('Speaker queue not enabled');
		}
		
		if (this.state.spectators.speakerQueue.some(s => s.uid === userId)) return;

		this.state.spectators.speakerQueue.push({
			uid: userId,
			requestedAt: Date.now()
		});

		this._bumpVersion('requestToSpeak');
	}

	admitSpeakerFromQueue(userRole, userId, teamId = 'Audience', durationSec = 60) {
		if (!this.can(userRole, 'manageSpectators')) {
			throw new Error('manageSpectators not allowed');
		}
		const index = this.state.spectators.speakerQueue.findIndex(s => s.uid === userId);
		if (index === -1) return;

		this.state.spectators.speakerQueue.splice(index, 1);

		this.state.spectators.activeGuestSpeakers[userId] = {
		teamId,
		expiresAt: Date.now() + durationSec * 1000
		};
		this._emitEvent('SPEAKER_ADMITTED', {
		  speakerId: userId,
		  teamId
		});
	  this._bumpVersion('admitSpeaker');
	}


	_pruneExpiredGuestSpeakers() {
		const now = Date.now();
		const active = this.state.spectators.activeGuestSpeakers;
		for (const [uid, meta] of Object.entries(active)) {
			if (meta.expiresAt && meta.expiresAt <= now) delete active[uid];
			this._emitEvent('SPEAKER_EXPIRED', {
				speakerId: uid
			});
		}
		this.state.spectators.activeGuestSpeakers = active;
		this.state.spectators.speakerQueue = (this.state.spectators.speakerQueue || []).filter(s => !s.expiresAt || s.expiresAt > now);
	}
	/* ----------------------------------
	* Debug / Summary
	* ---------------------------------- */

	debateSummary() {
	return {
	  debateId: this.debateId,
	  type: this.debateType.id,
	  status: this.state.status,
	  turns: this.state.turns,
	  scores: this.state.scores,
	  winner: this.state.winner ?? null
	};
	}

	// -------------------------
	// ?? HYDRATION (FIX)
	// -------------------------
	hydrate(serializedState) {
	if (!serializedState) {
	  throw new Error('hydrate() called with null state');
	}

	// IMPORTANT:
	// 1. No version increment
	// 2. No timers started
	// 3. No turn logic executed
	// 4. No side effects

	this.state = JSON.parse(JSON.stringify(serializedState));
	}

	/* ----------------------------------
	* Serialization
	* ---------------------------------- */

	serialize() {
	return {
	  debateId: this.debateId,
	  debateTypeId: this.debateType.id,
	  state: this.state
	};
	}

	// -------------------------
	// RESET ENGINE
	// -------------------------
	reset() {
	this.state = this._initialState();
	}

	/** 
	* Ensures engine has a valid state.
	* Throws if not initialized.
	*/
	ensureInitialized() {
	if (!this.state) {
	  throw new Error('DebateEngine state is not initialized. Call reset() first.');
	}
	}

	static fromSerialized(data) {
	return new DebateEngine({
	  debateId: data.debateId,
	  debateTypeId: data.debateTypeId,
	  state: data.state
	});
	}
}
